import React from 'react';

export interface StockOption {
  symbol: string;
  name: string;
}

interface ControlPanelProps {
  stocks: StockOption[];
  selectedStock: string;
  onStockChange: (symbol: string) => void;
  chartType: 'line' | 'candlestick';
  onChartTypeChange: (type: 'line' | 'candlestick') => void;
  indicators: {
    showEMA12: boolean;
    showEMA26: boolean;
    showEMA50: boolean;
    showEMA200: boolean;
    showVolume: boolean;
  };
  onIndicatorChange: (indicator: string, value: boolean) => void;
  theme: 'light' | 'dark';
  onThemeChange: (theme: 'light' | 'dark') => void;
}

export const ControlPanel: React.FC<ControlPanelProps> = ({
  stocks,
  selectedStock,
  onStockChange,
  chartType,
  onChartTypeChange,
  indicators,
  onIndicatorChange,
  theme,
  onThemeChange
}) => {
  const styles = {
    panel: {
      width: '320px',
      height: 'fit-content',
      backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
      border: `1px solid ${theme === 'dark' ? '#374151' : '#D1D5DB'}`,
      borderRadius: '0.5rem',
      padding: '1.5rem',
      marginRight: '20px',
      boxShadow: theme === 'dark' 
        ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' 
        : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    section: {
      marginBottom: '24px',
    },
    sectionTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: theme === 'dark' ? '#F9FAFB' : '#111827',
      marginBottom: '12px',
      borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
      paddingBottom: '8px',
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      fontSize: '0.875rem',
      border: `1px solid ${theme === 'dark' ? '#374151' : '#D1D5DB'}`,
      borderRadius: '0.375rem',
      backgroundColor: theme === 'dark' ? '#374151' : '#FFFFFF',
      color: theme === 'dark' ? '#E5E7EB' : '#111827',
      outline: 'none',
      cursor: 'pointer',
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '16px',
    },
    button: {
      flex: 1,
      padding: '0.625rem 1rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      border: `1px solid ${theme === 'dark' ? '#374151' : '#D1D5DB'}`,
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      backgroundColor: theme === 'dark' ? '#374151' : '#FFFFFF',
      color: theme === 'dark' ? '#E5E7EB' : '#374151',
    },
    activeButton: {
      backgroundColor: theme === 'dark' ? '#3B82F6' : '#2563EB',
      borderColor: theme === 'dark' ? '#3B82F6' : '#2563EB',
      color: '#FFFFFF',
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '12px',
      padding: '0.5rem',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
    checkbox: {
      width: '18px',
      height: '1.125rem',
      marginRight: '12px',
      cursor: 'pointer',
      accentColor: theme === 'dark' ? '#3B82F6' : '#2563EB',
    },
    checkboxLabel: {
      fontSize: '0.875rem',
      color: theme === 'dark' ? '#E5E7EB' : '#374151',
      cursor: 'pointer',
      flex: 1,
    },
    colorIndicator: {
      width: '16px',
      height: '1rem',
      borderRadius: '50%',
      marginLeft: '8px',
    },
    themeToggle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.75rem',
      backgroundColor: theme === 'dark' ? '#374151' : '#F3F4F6',
      borderRadius: '0.375rem',
      cursor: 'pointer',
    },
    themeLabel: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: theme === 'dark' ? '#E5E7EB' : '#374151',
    },
    toggle: {
      width: '44px',
      height: '1.5rem',
      backgroundColor: theme === 'dark' ? '#3B82F6' : '#D1D5DB',
      borderRadius: '0.75rem',
      position: 'relative' as const,
      transition: 'background-color 0.3s ease',
    },
    toggleCircle: {
      width: '20px',
      height: '1.25rem',
      backgroundColor: '#FFFFFF',
      borderRadius: '50%',
      position: 'absolute' as const,
      top: '2px',
      left: theme === 'dark' ? '22px' : '2px',
      transition: 'left 0.3s ease',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
    stockInfo: {
      padding: '0.75rem',
      backgroundColor: theme === 'dark' ? '#111827' : '#F9FAFB',
      borderRadius: '0.375rem',
      marginTop: '12px',
    },
    stockSymbol: {
      fontSize: '1.125rem',
      fontWeight: '700',
      color: theme === 'dark' ? '#3B82F6' : '#2563EB',
      marginBottom: '4px',
    },
    stockName: {
      fontSize: '0.875rem',
      color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
    },
  };

  const selectedStockData = stocks.find(stock => stock.symbol === selectedStock);

  const indicatorColors: { [key: string]: string } = {
    showEMA12: '#FF6B35',
    showEMA26: '#F7931E',
    showEMA50: '#A855F7',
    showEMA200: '#EF4444',
    showVolume: '#10B981',
  };

  const indicatorLabels: { [key: string]: string } = {
    showEMA12: 'EMA 12',
    showEMA26: 'EMA 26',
    showEMA50: 'EMA 50',
    showEMA200: 'EMA 200',
    showVolume: 'Volume',
  };

  return (
    <div style={styles.panel}>
      {/* Stock Selection */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Stock Symbol</h3>
        <select
          style={styles.select}
          value={selectedStock}
          onChange={(e) => onStockChange(e.target.value)}
        >
          {stocks.map((stock) => (
            <option key={stock.symbol} value={stock.symbol}>
              {stock.symbol} - {stock.name}
            </option>
          ))}
        </select>
        
        {selectedStockData && (
          <div style={styles.stockInfo}>
            <div style={styles.stockSymbol}>{selectedStockData.symbol}</div>
            <div style={styles.stockName}>{selectedStockData.name}</div>
          </div>
        )}
      </div>

      {/* Chart Type Selection */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Chart Type</h3>
        <div style={styles.buttonGroup}>
          <button
            style={{
              ...styles.button,
              ...(chartType === 'line' ? styles.activeButton : {}),
            }}
            onClick={() => onChartTypeChange('line')}
          >
            Line Chart
          </button>
          <button
            style={{
              ...styles.button,
              ...(chartType === 'candlestick' ? styles.activeButton : {}),
            }}
            onClick={() => onChartTypeChange('candlestick')}
          >
            Candlestick
          </button>
        </div>
      </div>

      {/* Technical Indicators */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Technical Indicators</h3>
        {Object.entries(indicators).map(([key, value]) => (
          <div
            key={key}
            style={{
              ...styles.checkboxContainer,
              backgroundColor: value 
                ? (theme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(37, 99, 235, 0.05)')
                : 'transparent',
            }}
            onMouseEnter={(e) => {
              if (!value) {
                e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(243, 244, 246, 0.8)';
              }
            }}
            onMouseLeave={(e) => {
              if (!value) {
                e.currentTarget.style.backgroundColor = 'transparent';
              } else {
                e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(37, 99, 235, 0.05)';
              }
            }}
            onClick={() => onIndicatorChange(key, !value)}
          >
            <input
              type="checkbox"
              style={styles.checkbox}
              checked={value}
              onChange={(e) => onIndicatorChange(key, e.target.checked)}
            />
            <span style={styles.checkboxLabel}>
              {indicatorLabels[key]}
            </span>
            {indicatorColors[key] && (
              <div
                style={{
                  ...styles.colorIndicator,
                  backgroundColor: indicatorColors[key],
                  opacity: value ? 1 : 0.3,
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Theme Toggle */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Appearance</h3>
        <div style={styles.themeToggle} onClick={() => onThemeChange(theme === 'light' ? 'dark' : 'light')}>
          <span style={styles.themeLabel}>
            {theme === 'light' ? '☀️ Light Mode' : '🌙 Dark Mode'}
          </span>
          <div style={styles.toggle}>
            <div style={styles.toggleCircle} />
          </div>
        </div>
      </div>
    </div>
  );
};





